import { html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import Styles from './vm-button.scss';

export type VmButtonVariant = 'solid' | 'outline' | 'ghost' | 'unstyled';
export type VmButtonColorScheme = 'primary' | 'secondary' | 'neutral' | 'error';

/**
 * Discriminated union of the valid (variant, colorScheme) combinations per
 * FE-16. Use when constructing props programmatically for compile-time
 * checking. HTML template attribute usage isn't checked by TS — an invalid
 * combo emits a runtime dev-mode warning instead.
 */
export type VmButtonConfig =
    | { variant: 'solid'; colorScheme: 'primary' | 'secondary' | 'error' }
    | { variant: 'outline'; colorScheme: 'neutral' | 'error' }
    | { variant: 'ghost'; colorScheme: 'neutral' | 'error' }
    | { variant: 'unstyled'; colorScheme: 'primary' | 'neutral' | 'error' };

const VALID_COMBOS = new Set<`${VmButtonVariant}:${VmButtonColorScheme}`>([
    'solid:primary',
    'solid:secondary',
    'solid:error',
    'outline:neutral',
    'outline:error',
    'ghost:neutral',
    'ghost:error',
    'unstyled:primary',
    'unstyled:neutral',
    'unstyled:error',
]);

/**
 * A button component following the Vumedi Moderat design system.
 *
 * @slot - Default slot for the button label text.
 * @slot icon-leading - Slot for a leading icon (before the label).
 * @slot icon-trailing - Slot for a trailing icon (after the label).
 *
 * @csspart button - The inner button or anchor element.
 *
 * @example
 * ```html
 * <vm-button variant="solid" color-scheme="primary" size="md">Click me</vm-button>
 *
 * <vm-button variant="solid" color-scheme="secondary" size="sm">
 *   <vm-icon slot="icon-leading" name="arrow-narrow-left"></vm-icon>
 *   Back
 * </vm-button>
 *
 * <vm-button variant="solid" color-scheme="primary" icon-only aria-label="Search">
 *   <vm-icon slot="icon-leading" name="search"></vm-icon>
 * </vm-button>
 *
 * <vm-button href="/settings" variant="unstyled" color-scheme="primary">Settings</vm-button>
 *
 * <vm-button variant="solid" color-scheme="error">Delete</vm-button>
 * <vm-button variant="outline" color-scheme="error">Remove</vm-button>
 * ```
 */
@customElement('vm-button')
export class VmButton extends LitElement {
    static override styles = Styles;

    /**
     * Visual shape. Not every (variant, colorScheme) combination is valid — see
     * {@link VmButtonConfig}. Invalid combos emit a runtime dev-mode warning.
     */
    @property({ reflect: true })
    variant: VmButtonVariant = 'solid';

    /**
     * Palette applied on top of `variant`. Only combinations listed in
     * {@link VmButtonConfig} are supported.
     */
    @property({ reflect: true, attribute: 'color-scheme' })
    colorScheme: VmButtonColorScheme = 'primary';

    /**
     * Size variant. `xs` is reserved for icon-only buttons (24×24 with a 12×12 icon);
     * setting `size="xs"` without `icon-only` has no effect.
     */
    @property({ reflect: true })
    size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';

    /** Whether the button is disabled. */
    @property({ type: Boolean, reflect: true })
    disabled = false;

    /** Shows a loading spinner and prevents interaction. */
    @property({ type: Boolean, reflect: true })
    loading = false;

    /** Renders as a square icon-only button. Provide an aria-label for accessibility. */
    @property({ type: Boolean, reflect: true, attribute: 'icon-only' })
    iconOnly = false;

    /** Stretches the button to fill its container width. */
    @property({ type: Boolean, reflect: true, attribute: 'full-width' })
    fullWidth = false;

    /** Button type for form participation. */
    @property()
    type: 'button' | 'submit' | 'reset' = 'button';

    /** When set, renders as an anchor element instead of a button. */
    @property()
    href?: string;

    /** Target for anchor navigation. */
    @property()
    target?: string;

    /** Rel attribute for anchor. */
    @property()
    rel?: string;

    /** Download attribute for anchor. */
    @property()
    download?: string;

    /** Accessible label. Required for icon-only buttons. */
    @property({ attribute: 'aria-label' })
    override ariaLabel: string | null = null;

    /** Block clicks when disabled or loading (capture phase). */
    private _handleHostClick = (e: MouseEvent): void => {
        if (this.disabled || this.loading) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    };

    /**
     * Proxy form submission/reset since a <button> inside Shadow DOM
     * cannot natively participate in ancestor form submission.
     */
    private _handleButtonClick = (): void => {
        if (this.type === 'button') return;

        const form = this.closest('form');
        if (!form) return;

        if (this.type === 'submit') {
            const proxy = document.createElement('button');
            proxy.type = 'submit';
            proxy.hidden = true;
            form.appendChild(proxy);
            proxy.click();
            proxy.remove();
        } else if (this.type === 'reset') {
            form.reset();
        }
    };

    override connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener('click', this._handleHostClick, { capture: true });
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('click', this._handleHostClick, { capture: true });
    }

    override willUpdate(changed: PropertyValues<this>): void {
        if (import.meta.env.DEV && (changed.has('variant') || changed.has('colorScheme'))) {
            const key: `${VmButtonVariant}:${VmButtonColorScheme}` = `${this.variant}:${this.colorScheme}`;
            if (!VALID_COMBOS.has(key)) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[vm-button] unsupported combination variant="${this.variant}" color-scheme="${this.colorScheme}".`,
                );
            }
        }
    }

    private get _buttonContent() {
        return html`
            <slot name="icon-leading"></slot>
            <span class="label"><slot></slot></span>
            <slot name="icon-trailing"></slot>
        `;
    }

    private _renderButton() {
        return html`
            <button
                type=${this.type}
                ?disabled=${this.disabled}
                aria-label=${ifDefined(this.ariaLabel ?? undefined)}
                aria-busy=${ifDefined(this.loading ? 'true' : undefined)}
                @click=${this._handleButtonClick}
                part="button"
            >
                <span class="content">${this._buttonContent}</span>
                ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : nothing}
            </button>
        `;
    }

    private _renderAnchor() {
        return html`
            <a
                href=${ifDefined(this.disabled ? undefined : this.href)}
                target=${ifDefined(this.target)}
                rel=${ifDefined(this.rel)}
                download=${ifDefined(this.download)}
                aria-label=${ifDefined(this.ariaLabel ?? undefined)}
                aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
                aria-busy=${ifDefined(this.loading ? 'true' : undefined)}
                tabindex=${ifDefined(this.disabled ? '-1' : undefined)}
                part="button"
            >
                <span class="content">${this._buttonContent}</span>
                ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : nothing}
            </a>
        `;
    }

    override render() {
        return this.href ? this._renderAnchor() : this._renderButton();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-button': VmButton;
    }
}
